
// src/components/Checkout.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PayPalButtons, usePayPalScriptReducer, FUNDING } from "@paypal/react-paypal-js";
import { useCart } from '../context/CartContext';
import LoginModal from '../components/LoginModal';
import jsPDF from 'jspdf';
import 'jspdf-autotable';


const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [{ isPending, isResolved, isRejected, options }, dispatch] = usePayPalScriptReducer();
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '', lastName: '', email: '', phone: '', address: '', city: '', postalCode: ''
  });

  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [completedOrderId, setCompletedOrderId] = useState(null);
  const [completedOrderDetails, setCompletedOrderDetails] = useState(null);
  const [paypalKey, setPaypalKey] = useState(Date.now());
  const [exchangeRate, setExchangeRate] = useState(0.055);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const [user, setUser] = useState(null);



  // Check user authentication - OPTIONAL (allows guest checkout)
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        // Pre-fill customer info with user data
        setCustomerInfo(prev => ({
          ...prev,
          firstName: userData.name?.split(' ')[0] || '',
          lastName: userData.name?.split(' ')[1] || '',
          email: userData.email || ''
        }));
      } catch (e) {
        console.error('Error parsing saved user:', e);
        // Continue as guest if user data is corrupted
      }
    }
    // ✅ GUEST CHECKOUT ENABLED - No forced login
  }, []);

  // Helper function to safely extract numeric price
  const extractNumericPrice = (price) => {
    if (price === null || price === undefined) {
      return 0;
    }
    
    if (typeof price === 'number') {
      return price;
    }
    
    if (typeof price === 'string') {
      const numericValue = parseFloat(price.replace(/[^0-9.-]+/g, ''));
      return isNaN(numericValue) ? 0 : numericValue;
    }
    
    // Try to convert other types
    try {
      const numericValue = parseFloat(price);
      return isNaN(numericValue) ? 0 : numericValue;
    } catch (error) {
      return 0;
    }
  };

  // Fixed formatPrice function
  const formatPrice = (priceString) => {
    return extractNumericPrice(priceString);
  };

  // Add debugging
  useEffect(() => {
    console.log('🧾 Checkout component - Debug cart items:');
    if (cart && cart.items) {
      cart.items.forEach((item, index) => {
        console.log(`  Item ${index + 1}:`, {
          name: item.name,
          price: item.price,
          type: typeof item.price,
          quantity: item.quantity,
          extracted: extractNumericPrice(item.price)
        });
      });
    }
  }, [cart]);

  // Add to your Checkout component
  useEffect(() => {
    // Suppress WebGPU errors (they're not critical)
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0] && args[0].toString().includes('WebGPU')) {
        return; // Ignore WebGPU errors
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  // ✅ Force reload PayPal script if currency is ZAR
  useEffect(() => {
    console.log('🔧 PayPal Script Options:', options);
    
    if (options.currency === 'ZAR') {
      console.error('❌ DETECTED ZAR CURRENCY - FORCING RELOAD');
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }, [options]);

  const convertToUSD = (zarAmount) => {
    return (zarAmount * exchangeRate).toFixed(2);
  };

  const handleInputChange = (e) => {
    setCustomerInfo({ ...customerInfo, [e.target.name]: e.target.value });
  };

  const validateCartItems = () => {
    return cart.items.every(item => {
      const price = extractNumericPrice(item.price); // FIXED: Use safe function
      return item.name && item.quantity > 0 && !isNaN(price) && price > 0;
    });
  };

  // ✅ Generate receipt text
  const generateReceipt = (orderData) => {
    const items = orderData.purchase_units[0].items || [];
    const total = orderData.purchase_units[0].amount.value;
    const subtotal = orderData.purchase_units[0].amount.breakdown.item_total.value;
    const tax = orderData.purchase_units[0].amount.breakdown.tax_total.value;
    
    return `
⚡ SOLAR ENERGY INVOICE ⚡
===========================

ORDER ID: ${orderData.id}
DATE: ${new Date(orderData.create_time || Date.now()).toLocaleString()}
STATUS: ${orderData.status}

CUSTOMER INFORMATION:
---------------------
Name: ${orderData.customerInfo?.firstName} ${orderData.customerInfo?.lastName}
Email: ${orderData.customerInfo?.email || orderData.payer?.email_address}
Phone: ${orderData.customerInfo?.phone}

SHIPPING ADDRESS:
-----------------
${orderData.customerInfo?.address}
${orderData.customerInfo?.city} ${orderData.customerInfo?.postalCode}

ORDER DETAILS:
--------------
${items.map(item => `${item.name} x${item.quantity} - $${item.unit_amount.value}`).join('\n')}

SUBTOTAL: $${subtotal}
TAX: $${tax}
SHIPPING: $${orderData.purchase_units[0].amount.breakdown.shipping.value}
TOTAL: $${total} USD

PAYMENT METHOD: PayPal
PAYER EMAIL: ${orderData.payer?.email_address}

===========================
Thank you for your purchase!
Please save this receipt for your records.

Contact: support@solarstore.com
    `;
  };

  // ✅ Save receipt as downloadable file
  const saveReceiptToFile = (receiptText, orderId) => {
    const blob = new Blob([receiptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Solar_Order_${orderId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };


  // ✅ Store order locally and send PDF email (NO BACKEND NEEDED)
  const saveOrderLocally = async (orderData) => {
    try {
      console.log('✅ PAYMENT COMPLETED! Storing order locally...', orderData);
      
      // 1. Store in localStorage
      const orders = JSON.parse(localStorage.getItem('paypal_orders') || '[]');
      orders.push({
        ...orderData,
        timestamp: new Date().toISOString(),
        synced: false
      });
      localStorage.setItem('paypal_orders', JSON.stringify(orders));
      
      // 2. Also store last order separately
      localStorage.setItem('lastOrder', JSON.stringify(orderData));
      
      // 3. Generate and download PDF receipt
      const pdfDoc = generatePDFReceipt(orderData);
      pdfDoc.save(`KingSolarman_Receipt_${orderData.id}.pdf`);
      
      // 4. Send PDF receipt via email
      const emailSent = await sendEmailWithPDF(orderData);
      if (emailSent) {
        console.log('📧 PDF receipt sent via email');
      } else {
        console.log('📧 Email not available, but PDF downloaded');
      }
      
      return true;
      
    } catch (error) {
      console.log('⚠️ Local storage successful, PDF/email may have failed');
      return true; // Still return true since payment was successful
    }
  };


  // ✅ Generate PDF receipt
  const generatePDFReceipt = (orderData) => {
    const doc = new jsPDF();
    
    // Add header
    doc.setFontSize(20);
    doc.setTextColor(44, 90, 160); // KingSolarman blue
    doc.text('KingSolarman', 105, 20, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setTextColor(100, 100, 100);
    doc.text('Order Receipt', 105, 30, { align: 'center' });
    
    // Order details
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    
    const orderId = orderData.id;
    const date = new Date(orderData.create_time || Date.now()).toLocaleDateString();
    const customerName = `${orderData.customerInfo?.firstName || ''} ${orderData.customerInfo?.lastName || ''}`.trim();
    const customerEmail = orderData.customerInfo?.email || orderData.payer?.email_address || '';
    
    doc.text(`Order ID: ${orderId}`, 20, 50);
    doc.text(`Date: ${date}`, 20, 60);
    doc.text(`Customer: ${customerName}`, 20, 70);
    doc.text(`Email: ${customerEmail}`, 20, 80);
    
    // Order items table
    const items = orderData.purchase_units?.[0]?.items || orderData.items || [];
    const total = orderData.purchase_units?.[0]?.amount?.value || '0.00';
    
    // Table headers
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Item', 20, 100);
    doc.text('Qty', 120, 100);
    doc.text('Price', 150, 100);
    doc.text('Total', 180, 100);
    
    // Draw line
    doc.line(20, 105, 190, 105);
    
    // Table rows
    doc.setFont(undefined, 'normal');
    let yPos = 115;
    
    items.forEach((item, index) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      const quantity = item.quantity || 1;
      const unitPrice = item.unit_amount?.value || item.price || '0.00';
      const itemTotal = (parseFloat(unitPrice) * quantity).toFixed(2);
      
      doc.text(item.name?.substring(0, 40) || 'Item', 20, yPos);
      doc.text(quantity.toString(), 120, yPos);
      doc.text(`$${unitPrice}`, 150, yPos);
      doc.text(`$${itemTotal}`, 180, yPos);
      yPos += 10;
    });
    
    // Total
    yPos += 10;
    doc.line(20, yPos, 190, yPos);
    yPos += 10;
    doc.setFont(undefined, 'bold');
    doc.text(`TOTAL: $${total} USD`, 150, yPos);
    
    // Footer
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Thank you for your purchase!', 105, 280, { align: 'center' });
    doc.text('Contact: info@kingsolarman.co.bw | +267 7154 8257', 105, 290, { align: 'center' });
    
    return doc;
  };


  // ✅ Send email using nodemailer backend
  const sendEmailWithPDF = async (orderData) => {
    try {
      // Call backend nodemailer endpoint
      const response = await fetch('http://localhost:5000/api/email/order-confirmation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ orderData })
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        console.log('📧 Order confirmation email sent successfully:', result.messageId);
        return true;
      } else {
        console.error('❌ Failed to send order confirmation email:', result.error);
        return false;
      }
      
    } catch (error) {
      console.error('❌ Failed to send PDF email:', error);
      return false;
    }
  };

  // ✅ Send test email (for debugging)
  const sendTestEmail = async (email) => {
    try {
      const response = await fetch('http://localhost:5000/api/email/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        console.log('📧 Test email sent successfully:', result.messageId);
        return true;
      } else {
        console.error('❌ Failed to send test email:', result.error);
        return false;
      }
      
    } catch (error) {
      console.error('❌ Failed to send test email:', error);
      return false;
    }
  };

  const createOrder = (data, actions) => {
    console.log('🚀 Creating PayPal order with currency:', options.currency);
    
    if (!validateCartItems()) {
      alert('Some cart items are invalid. Please check your cart.');
      return Promise.reject(new Error('Invalid cart items'));
    }

    const subtotalZAR = getCartTotal();
    const taxZAR = subtotalZAR * 0.15;
    const totalZAR = subtotalZAR + taxZAR;
    
    // Convert to USD - USE PROPER ROUNDING
    const subtotalUSD = parseFloat(convertToUSD(subtotalZAR));
    const taxUSD = parseFloat(convertToUSD(taxZAR));
    const totalUSD = parseFloat(convertToUSD(totalZAR));
    
    // ✅ Calculate total from items to verify
    const itemTotalUSD = cart.items.reduce((sum, item) => {
      const itemPriceZAR = formatPrice(item.price);
      const itemPriceUSD = parseFloat(convertToUSD(itemPriceZAR));
      return sum + (itemPriceUSD * item.quantity);
    }, 0);
    
    const calculatedTotal = parseFloat((itemTotalUSD + taxUSD).toFixed(2));
    
    console.log('💱 Currency Conversion Debug:', {
      subtotalZAR, taxZAR, totalZAR,
      subtotalUSD, taxUSD, totalUSD,
      itemTotalUSD, calculatedTotal,
      match: Math.abs(totalUSD - calculatedTotal) < 0.01
    });

    // ✅ Use calculated total to avoid mismatch
    const finalTotal = calculatedTotal;

    const purchaseUnits = [{
      description: "Solar Inverters Purchase",
      amount: {
        currency_code: "USD",
        value: finalTotal.toFixed(2),
        breakdown: {
          item_total: { currency_code: "USD", value: itemTotalUSD.toFixed(2) },
          tax_total: { currency_code: "USD", value: taxUSD.toFixed(2) },
          shipping: { currency_code: "USD", value: "0.00" },
          handling: { currency_code: "USD", value: "0.00" },
          insurance: { currency_code: "USD", value: "0.00" },
          shipping_discount: { currency_code: "USD", value: "0.00" },
          discount: { currency_code: "USD", value: "0.00" }
        }
      },
      items: cart.items.map(item => {
        const itemPriceZAR = formatPrice(item.price);
        const itemPriceUSD = parseFloat(convertToUSD(itemPriceZAR)).toFixed(2);
        
        return {
          name: item.name.substring(0, 127),
          // description: (item.brand || "Solar Inverter").substring(0, 127),
          quantity: item.quantity.toString(),
          unit_amount: { 
            currency_code: "USD", 
            value: itemPriceUSD 
          },
          // category: "PHYSICAL_GOODS"
        };
      })
    }];

    console.log('📊 Purchase Unit:', JSON.stringify(purchaseUnits, null, 2));
    
    return actions.order.create({
      purchase_units: purchaseUnits,
      application_context: { shipping_preference: "NO_SHIPPING" }
    });
  };


  const onApprove = (data, actions) => {
    return actions.order.capture().then(async function(details) {
      console.log('💰 Payment completed:', details);
      
      // ✅ Prepare order data
      const orderData = {
        id: details.id,
        status: details.status,
        purchase_units: details.purchase_units,
        payer: details.payer,
        create_time: details.create_time,
        update_time: details.update_time,
        customerInfo: customerInfo,
        items: cart.items,
        shippingAddress: {
          address: customerInfo.address,
          city: customerInfo.city,
          postalCode: customerInfo.postalCode
        }
      };


      // ✅ Skip Django API authentication issue - use local storage only
      console.log('💾 Storing order locally due to authentication requirement');
      console.log('📋 Order stored locally with ID:', orderData.id);
      orderData.djangoOrderId = `LOCAL-${orderData.id}`;
      orderData.storageMethod = 'local-only';
      
      // Note: Django API requires authentication, so we store locally
      // In production, this would require either:
      // 1. Public order endpoint on Django backend
      // 2. User authentication system
      // 3. Admin API key for order processing

      // ✅ Send WhatsApp notification via local backend
      try {
        console.log('📱 Sending WhatsApp notification...');

        const whatsappResponse = await fetch('http://localhost:5001/api/notifications/whatsapp', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(orderData)
        });

        const whatsappResult = await whatsappResponse.json();
        
        if (whatsappResult.success) {
          console.log('✅ WhatsApp notification sent to +26771548257');
          orderData.whatsappNotification = 'Sent to +26771548257';
        } else {
          console.error('❌ Failed to send WhatsApp notification:', whatsappResult.error);
        }
      } catch (whatsappError) {
        console.error('❌ WhatsApp notification error:', whatsappError);
        // Continue with other processes even if WhatsApp fails
      }

      // ✅ Store locally as backup
      await saveOrderLocally(orderData);

      // ✅ Update state
      setCompletedOrderId(details.id);
      setCompletedOrderDetails(orderData);
      setOrderCompleted(true);
      clearCart();
      


      // ✅ Show success message
      alert(`🎉 Payment completed successfully!\nOrder ID: ${details.id}\n\n✅ PDF receipt downloaded\n✅ Email sent with PDF receipt\n✅ Order sent to Django API\n✅ WhatsApp notification sent to +26771548257`);
      
    }).catch(err => {
      console.error('❌ Capture error:', err);
      alert('Payment capture failed. Please try again.');
    });
  };

  const onError = (err) => {
    console.error('❌ PayPal Checkout Error:', err);
    
    if (err.message?.includes('currency') || err.message?.includes('Unexpected currency')) {
      alert('Currency configuration error. The page will reload to fix this.');
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else {
      alert('There was an error processing your payment. Please try again.');
    }
  };

  const onCancel = (data) => {
    console.log('🚫 Payment cancelled:', data);
    alert('Payment was cancelled. You can try again when ready.');
  };

  const handleManualOrder = (e) => {
    e.preventDefault();
    if (!customerInfo.firstName || !customerInfo.lastName || !customerInfo.email || 
        !customerInfo.phone || !customerInfo.address || !customerInfo.city || !customerInfo.postalCode) {
      alert('Please fill in all required fields.');
      return;
    }
    
    // For manual orders, create local order
    const manualOrderData = {
      id: `MANUAL-${Date.now()}`,
      status: 'MANUAL',
      purchase_units: [{
        amount: {
          value: (getCartTotal() * 1.15).toFixed(2),
          currency_code: 'ZAR',
          breakdown: {
            item_total: { value: getCartTotal().toFixed(2), currency_code: 'ZAR' },
            tax_total: { value: (getCartTotal() * 0.15).toFixed(2), currency_code: 'ZAR' },
            shipping: { value: "0.00", currency_code: 'ZAR' }
          }
        },
        items: cart.items
      }],
      payer: {
        email_address: customerInfo.email,
        name: { given_name: customerInfo.firstName, surname: customerInfo.lastName }
      },
      customerInfo: customerInfo,
      items: cart.items,
      create_time: new Date().toISOString()
    };
    

    // Save locally and send PDF
    saveOrderLocally(manualOrderData);
    
    alert('Order placed successfully! PDF receipt downloaded and sent via email.');
    clearCart();
    setOrderCompleted(true);
    setCompletedOrderDetails(manualOrderData);
  };

  // Debug component
  const PayPalDebugInfo = () => (
    <div style={{ 
      background: options.currency === 'USD' ? '#d4edda' : '#f8d7da', 
      padding: '10px', 
      borderRadius: '5px', 
      marginBottom: '15px', 
      fontSize: '12px', 
      border: `1px solid ${options.currency === 'USD' ? '#c3e6cb' : '#f5c6cb'}`,
      color: options.currency === 'USD' ? '#155724' : '#721c24'
    }}>
      <strong>PayPal Debug Info:</strong>
      <div>Script Currency: <strong style={{color: options.currency === 'USD' ? 'green' : 'red'}}>{options.currency}</strong></div>
      <div>Client ID: {options['client-id'] ? '✓ Set' : '✗ Missing'}</div>
      <div>Status: {isPending ? '🔄 Loading' : isResolved ? '✅ Ready' : '❌ Error'}</div>
      <div>Storage: {localStorage.getItem('paypal_orders') ? `${JSON.parse(localStorage.getItem('paypal_orders') || '[]').length} orders saved` : 'No orders yet'}</div>
      {options.currency === 'ZAR' && (
        <div style={{color: 'red', fontWeight: 'bold', marginTop: '5px'}}>
          ❌ WRONG CURRENCY - Page will reload automatically...
        </div>
      )}
      {options.currency === 'USD' && (
        <div style={{color: 'green', fontWeight: 'bold', marginTop: '5px'}}>
          ✅ CORRECT CURRENCY - PayPal should work now!
        </div>
      )}
    </div>
  );

  if (orderCompleted) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="order-success">
            <div className="success-icon">✓</div>
            <h1>Order Confirmed!</h1>
            <p>Thank you for your purchase. Your order has been successfully processed.</p>
            {completedOrderId && (
              <div style={{ textAlign: 'left', margin: '20px 0', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                <p><strong>Order ID:</strong> {completedOrderId}</p>
                <p><strong>Status:</strong> {completedOrderDetails?.status}</p>
                <p><strong>Amount:</strong> ${completedOrderDetails?.purchase_units?.[0]?.amount?.value || 'N/A'} USD</p>
                <p><strong>Date:</strong> {new Date().toLocaleString()}</p>

                <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                  ✅ PDF receipt has been automatically downloaded<br/>
                  ✅ PDF receipt has been sent to your email address<br/>
                  Check your downloads folder for "KingSolarman_Receipt_{completedOrderId}.pdf"
                </p>
              </div>
            )}
            <div style={{ margin: '20px 0' }}>

              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  if (completedOrderDetails) {
                    const pdfDoc = generatePDFReceipt(completedOrderDetails);
                    pdfDoc.save(`KingSolarman_Receipt_${completedOrderId}.pdf`);
                  }
                }}
                style={{ marginRight: '10px' }}
              >
                Download PDF Receipt Again
              </button>
              <a href="/" className="btn btn-primary">Continue Shopping</a>
            </div>
            <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '20px' }}>
              All orders are stored locally in your browser. To view all orders, add /admin to your URL.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="checkout-page">
        <div className="container">
          <h1>Checkout</h1>
          <p>Your cart is empty. Please add items to your cart before checking out.</p>
        </div>
      </div>
    );
  }

  const subtotalZAR = getCartTotal();
  const taxZAR = subtotalZAR * 0.15;
  const totalZAR = subtotalZAR + taxZAR;

  return (
    <div className="checkout-page">
      <div className="container">
        <h1>Checkout</h1>
        
        <PayPalDebugInfo />
        
        {/* Currency Notice */}
        <div style={{ 
          background: '#fff3cd', 
          padding: '15px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #ffeaa7',
          color: '#856404'
        }}>
          <strong>💱 Currency Notice:</strong> Payments are processed in USD. 
          Your total of <strong>P {totalZAR.toLocaleString()}</strong> is approximately 
          <strong> ${convertToUSD(totalZAR)} USD</strong>.
        </div>
        
        <div className="checkout-content">
          <div className="checkout-form">
            <form onSubmit={handleManualOrder}>
              <div className="form-section">
                <h3>Contact Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input type="text" name="firstName" value={customerInfo.firstName} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input type="text" name="lastName" value={customerInfo.lastName} onChange={handleInputChange} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input type="email" name="email" value={customerInfo.email} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input type="tel" name="phone" value={customerInfo.phone} onChange={handleInputChange} required />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Shipping Address</h3>
                <div className="form-group">
                  <label>Address *</label>
                    <input type="text" name="address" value={customerInfo.address} onChange={handleInputChange} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City *</label>
                    <input type="text" name="city" value={customerInfo.city} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Postal Code *</label>
                    <input type="text" name="postalCode" value={customerInfo.postalCode} onChange={handleInputChange} required />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Payment Method</h3>
                <div className="payment-methods">
                  <label className="payment-option">
                    <input type="radio" name="paymentMethod" value="paypal" checked={paymentMethod === 'paypal'} onChange={(e) => setPaymentMethod(e.target.value)} />
                    <span>PayPal & Credit/Debit Cards</span>
                  </label>
                  <label className="payment-option">
                    <input type="radio" name="paymentMethod" value="credit-card" checked={paymentMethod === 'credit-card'} onChange={(e) => setPaymentMethod(e.target.value)} />
                    <span>Credit Card (Manual)</span>
                  </label>
                  <label className="payment-option">
                    <input type="radio" name="paymentMethod" value="bank-transfer" checked={paymentMethod === 'bank-transfer'} onChange={(e) => setPaymentMethod(e.target.value)} />
                    <span>Bank Transfer</span>
                  </label>
                </div>

                {paymentMethod === 'paypal' && (
                  <div className="paypal-section">
                    {isPending && (
                      <div className="paypal-loading">
                        <div>Loading payment options...</div>
                        <small>Please wait while we connect to PayPal</small>
                      </div>
                    )}
                    
                    {isRejected && (
                      <div className="paypal-error">
                        <h4>Unable to Load Payment Options</h4>
                        <p>There was a problem connecting to the payment processor.</p>
                        <button 
                          type="button" 
                          className="btn btn-secondary"
                          onClick={() => window.location.reload()}
                        >
                          Refresh Page
                        </button>
                      </div>
                    )}
                    
                    {isResolved && options.currency === 'USD' && (
                      <div className="payment-buttons">
                        {/* PayPal Button */}
                        <div style={{ marginBottom: '10px' }}>
                          <PayPalButtons
                            key={`paypal-${paypalKey}`}
                            style={{
                              layout: "vertical",
                              color: "blue",
                              shape: "rect",
                              label: "paypal",
                              height: 48
                            }}
                            fundingSource={FUNDING.PAYPAL}
                            createOrder={createOrder}
                            onApprove={onApprove}
                            onError={onError}
                            onCancel={onCancel}
                          />
                        </div>
                        
                        {/* Credit/Debit Card Button */}
                        <div style={{ marginBottom: '10px' }}>
                          <PayPalButtons
                            key={`card-${paypalKey}`}
                            style={{
                              layout: "vertical",
                              color: "black",
                              shape: "rect",
                              label: "checkout",
                              height: 48
                            }}
                            fundingSource={FUNDING.CARD}
                            createOrder={createOrder}
                            onApprove={onApprove}
                            onError={onError}
                            onCancel={onCancel}
                          />
                        </div>
                        
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#6c757d', 
                          textAlign: 'center',
                          marginTop: '10px'
                        }}>
                          💳 Pay with PayPal, Credit, or Debit Card
                        </div>
                      </div>
                    )}
                    
                    {isResolved && options.currency !== 'USD' && (
                      <div className="paypal-error">
                        <p>Currency configuration error. Please refresh the page.</p>
                      </div>
                    )}
                  </div>
                )}

                {paymentMethod !== 'paypal' && (
                  <button type="submit" className="place-order-btn btn-primary">
                    Place Order - P {totalZAR.toLocaleString()}
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="order-summary">
            <h3>Order Summary</h3>
            <div className="order-items">
              {cart.items.map(item => (
                <div key={item.id} className="order-item">
                  <div className="item-info">
                    <span className="item-name">{item.name}</span>
                    <span className="item-quantity">Qty: {item.quantity}</span>
                  </div>
                  <span className="item-price">P {(formatPrice(item.price) * item.quantity).toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div className="order-totals">
              <div className="total-row"><span>Subtotal:</span><span>P {subtotalZAR.toLocaleString()}</span></div>
              <div className="total-row"><span>Shipping:</span><span>P 0.00</span></div>
              <div className="total-row"><span>Tax (15%):</span><span>P {taxZAR.toLocaleString()}</span></div>
              <div className="total-row grand-total"><span>Total (BWP):</span><span>P {totalZAR.toLocaleString()}</span></div>
              <div className="total-row usd-total"><span>Total (USD):</span><span>${convertToUSD(totalZAR)} USD</span></div>
            </div>
          </div>

        </div>
      </div>



      {/* Optional Login Section - For users who want to log in */}
      {!user && (
        <div style={{
          background: '#e7f3ff',
          padding: '20px',
          borderRadius: '8px',
          marginTop: '20px',
          border: '1px solid #b3d9ff'
        }}>
          <h4>💡 Want to save your order history?</h4>
          <p style={{ marginBottom: '15px', color: '#495057' }}>
            Log in to automatically save your order details and track future purchases.
          </p>
          <button 
            className="btn btn-outline-primary"
            onClick={() => setShowLoginModal(true)}
            style={{ marginRight: '10px' }}
          >
            Log In (Optional)
          </button>
          <span style={{ fontSize: '14px', color: '#6c757d' }}>
            You can also create an account after completing your purchase.
          </span>
          
          {/* Optional Login Modal */}
          <LoginModal 
            isOpen={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            title="Log In (Optional)"
            message="Log in to save your order history and track future purchases. You can also proceed without logging in."
            isMandatory={false}
          />
        </div>
      )}
    </div>
  );
};

export default Checkout;